import React, { useState, useEffect, Component, useMemo, useRef } from 'react';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import useMediaQuery from '@mui/material/useMediaQuery';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTheme } from '@mui/material/styles';
import { AdvancedPointerView } from '..';
import { Listing, OccupancyRate, ADR, Revenue, ListingLegacy } from 'src/interfaces/idashboard';
import { IndividualListingModalPropTypes } from 'src/interfaces/iproptypes';
// import PerformanceData from 'src/App/Components/PerformanceData';
import PerformanceData from './PerformanceData';
import TopBar from './TopBar';
import HeroTitleSection from './HeroTitleSection';
import BarGraphData from './BarGraphData';
import Ameneties from './Ameneties';
import styles from './style';
import {
  processManagerWithId,
  getMonthlyKeyMetrics,
  isListingLegacy,
  checkListingRecentlyAdded,
  checkListingInactive,
  checkListingUnlisted,
  checkAdrAnomaly,
  processIndividualListingData,
} from 'src/utils/functions';
import { useDispatch, useSelector } from 'react-redux';
import DisabledBlurCard from 'src/App/Pages/EstimatorPage/UtilityComponents/BlurCard';
import HistoricalPerformanceGraph from './HistoricalPerformanceGraph';
import {
  COLOR_LIGHT_PURPLE,
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_RED,
  NEIGHBOURHOOD_TOOLTIP_Z_INDEX,
  SAMPLE_LISTING_DATA,
} from 'src/const';
import { CircularProgress, Container, IconButton, Tooltip, Typography } from '@mui/material';
import TabBar from 'src/App/Components/Tabs/TabBar';
import {
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_SECONDARY_1_400,
  WATER_MARK_COLOR,
} from 'src/colors';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import DummyListingImage1 from 'src/App/assets/listing_dummy_1.jpg';
import DummyListingImage2 from 'src/App/assets/listing_dummy_2.jpg';
import DummyListingImage3 from 'src/App/assets/listing_dummy_3.jpg';
import BookingTypeDemoGraph from 'src/App/assets/booking_type_demo_graph.jpg';
import ListingDetailsSection from './ListingDetailsSection';
import { User } from 'src/interfaces/iuser';
import { callApi } from 'src/core/callApi';

const SubSectionTabs = ({ isMobile, tabList, currentTabView, isMobileScreen }) => {
  return (
    <>
      <TabBar
        tabList={tabList}
        currentTab={currentTabView}
        color={COLOR_PRIMARY_2}
        renderLabelCondition={() => true}
        actionButton={
          <Tooltip title={'Save Listing (Coming Soon)!'}>
            <FavoriteBorderRoundedIcon
              sx={{
                color: COLOR_RED,
                padding: '15px',
                borderRadius: '800px',
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                '&:hover': {
                  backgound: '#ccc',
                },
              }}
            />
          </Tooltip>
        }
        showActionButton={isMobileScreen ? false : true}
        isMobile={isMobile}
        scrollButtons={isMobile}
      />
    </>
  );
};

const ComingSoonOverlay = ({ additionalStyling }: { additionalStyling?: any }) => {
  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          color: WATER_MARK_COLOR,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontWeight: 'bold',
          textShadow: '0 1px 0 rgba(255, 255, 255, 0.4)',
          ...(additionalStyling && additionalStyling),
        }}
      >
        Coming Soon
      </Typography>
    </Box>
  );
};

const IndividualListingModalV2 = ({
  open,
  onClose,
  data,
  currency,
  isDisabled,
  listings,
  subscriptionModalOpen,
  setSubscriptionModalOpen,
  handleOnSampleClick,
  multipleBookingMarkersCallLoading,
  estimatorSampleListingData,
  isEstimator,
  openDemoBookingForm,
  setDemoFormHeadingFor,
  isHistoricalDataHidden,
  onEstimatorLockedMarkerHistoricalDataClick,
  noListingData,
  referenceADR,
  cachedMarketData,
}: IndividualListingModalPropTypes) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const currentMarketUrl = useRef(window.location.pathname + window.location.search);

  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const [advancedViewOpen, setAdvancedViewOpen] = useState<boolean>(false);
  const [ownerTotalListings, setOwnerTotalListings] = useState<Listing[] | ListingLegacy[]>([]);

  const [dynamicOccupancyRate, setDynamicOccupancyRate] = useState<OccupancyRate[]>([]);
  const [dynamicADR, setDynamicADR] = useState<ADR[]>([]);
  const [dynamicRev, setDynamicRev] = useState<Revenue[]>([]);

  const [currentTabView, setCurrentTabView] = useState<
    'listing' | 'overview' | 'seasonality' | 'bookingDetails' | 'similarListings'
  >('overview');

  const [historicalPerfTrigger, setHistoricalPerfTrigger] = useState(false);

  // Initially set to null to prevent the re-rendering (with null value check) of its dependency load while initial page load
  const [isDemo, setIsDemo] = useState<boolean | null>(null);
  const [currentListingData, setCurrentListingData] = useState<any[] | Listing>(data);

  const [showLoadingScreen, setShowLoadingScreen] = useState<boolean>(false);

  const user: User = useSelector((state: any) => state.dashboard.user);

  const refetchListingData = async (listingId: string) => {
    const URL = 'https://8q15spida4.execute-api.us-east-2.amazonaws.com/default/getListingDetails';
    const config = {
      queryParams: {
        listingID: listingId,
      },
    };
    const resp = await callApi(URL, config, true);
    const listingData = resp?.message;

    if (listingData) {
      const transformedData = processIndividualListingData(listingData);
      return transformedData;
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (
      !Object.keys(data).includes('rest_thumbnails') ||
      !data['rest_thumbnails'] ||
      data['rest_thumbnails'].length === 0
    ) {
      const listingId = data['listingID'];
      refetchListingData(listingId).then(resp => {
        if (resp) {
          setCurrentListingData(resp);
        }
      });
    }
  }, []);

  useEffect(() => {
    setCurrentListingData(isDemo ? SAMPLE_LISTING_DATA : data);
    toggleUrlByDemoListingId(isDemo ? 'set' : 'unset');

    isDemo !== null && setShowLoadingScreen(true);
    const timeout = setTimeout(() => {
      isDemo !== null && setShowLoadingScreen(false);
    }, 300);

    return () => timeout && clearTimeout(timeout);
  }, [isDemo]);

  useEffect(() => {
    if (subscriptionModalOpen) {
      onClose();
    }
  }, [subscriptionModalOpen]);

  const setListingUrl = (listingId: string) => {
    const newPathName = `/listing/${listingId}`;
    window.history.replaceState(null, '', newPathName);
  };

  const setMarketUrlBack = () => {
    currentMarketUrl.current && window.history.replaceState(null, '', currentMarketUrl.current);
  };

  const toggleUrlByDemoListingId = (action: 'set' | 'unset') => {
    if (action == 'set') {
      const newPathName = `/listing/${SAMPLE_LISTING_DATA.listingID}`;
      window.history.replaceState(null, '', newPathName);
    } else {
      const newPathName = `/listing/${data['listingID']}`;
      window.history.replaceState(null, '', newPathName);
    }
  };

  useEffect(() => {
    if (noListingData) return;

    setListingUrl(data['listingID']);
    return () => {
      setMarketUrlBack();
    };
  }, []);

  const isListingRecentlyAdded = checkListingRecentlyAdded(currentListingData as Listing);
  const isListingInactive = checkListingInactive(currentListingData as Listing);
  const isListingUnlisted = checkListingUnlisted(currentListingData as Listing);
  const isAdrAnomaly = checkAdrAnomaly(
    (currentListingData as Listing).adrLTM,
    referenceADR || null
  );

  const ListingTabSection = (
    <ListingDetailsSection
      data={currentListingData}
      isMobileScreen={isMobileScreen}
      cachedMarketData={cachedMarketData}
    />
  );

  const PerformanceTabSection = (
    <Grid
      container
      direction="row"
      justifyContent="center"
      sx={{ width: isMobileScreen ? '100%' : '100%' }}
      spacing={isMobileScreen ? 0 : 4}
      xs={12}
    >
      <PerformanceData
        data={currentListingData}
        currency={currency}
        handleOnSampleClick={handleOnSampleClick}
        isDisabled={isDisabled}
        isEstimator={isEstimator}
        isListingRecentlyAdded={isListingRecentlyAdded}
        isMobileScreen={isMobileScreen}
        isAdrAnomaly={isAdrAnomaly}
        setMarketUrlBack={setMarketUrlBack}
        onClose={onClose}
        isDemo={isDemo}
        setIsDemo={setIsDemo}
      />
    </Grid>
  );

  const SeasonalityTabSection = (
    <Grid
      container
      direction="row"
      justifyContent="flex-start"
      sx={{ width: isMobileScreen ? '100%' : '100%', marginTop: '10px', padding: '0 0 20px 20px' }}
      spacing={isMobileScreen ? 0 : 4}
      xs={12}
    >
      <HistoricalPerformanceGraph
        listingID={currentListingData['listingID']}
        handleOnSampleClick={handleOnSampleClick}
        currency={currency}
        setMarketUrlBack={setMarketUrlBack}
        triggerModalClose={onClose}
        isAdrAnomaly={isAdrAnomaly}
        showOccupancyGraphOnly={false}
        isDemo={isDemo}
        setIsDemo={setIsDemo}
      />
    </Grid>
  );

  const SimilarListingTabSection = (
    <Grid
      container
      direction="row"
      justifyContent="center"
      sx={{
        position: 'relative',
        gap: '20px',
        width: '100%',
        marginTop: '10px',
        padding: '0 20px 0 20px',
      }}
      xs={12}
    >
      <img src={DummyListingImage1} style={{ maxWidth: '220px', width: '100%' }} />
      <img src={DummyListingImage2} style={{ maxWidth: '220px', width: '100%' }} />
      <img src={DummyListingImage3} style={{ maxWidth: '220px', width: '100%' }} />
      <ComingSoonOverlay />
    </Grid>
  );

  const BookingDetailsTabSection = (
    <Grid
      container
      direction="row"
      justifyContent="flex-start"
      sx={{
        position: 'relative',
        gap: '20px',
        width: '100%',
        marginTop: '10px',
        padding: '0 20px 0 20px',
      }}
      xs={12}
    >
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Booking Length Analysis
        </Typography>
      </Grid>
      <Grid item sx={{ position: 'relative', maxWidth: '500px', width: '100%' }}>
        <img src={BookingTypeDemoGraph} style={{ maxWidth: '220px', width: '100%' }} />
        <ComingSoonOverlay
          additionalStyling={{
            left: 0,
            transform: 'translate(0, -50%)',
          }}
        />
      </Grid>
    </Grid>
  );

  const tabList = useMemo(
    () => [
      {
        label: 'Overview',
        value: 'overview',
        component: PerformanceTabSection,
        onclick: () => {
          setCurrentTabView('overview');
        },
      },
      {
        label: 'Listing',
        value: 'listing',
        component: ListingTabSection,
        onclick: () => {
          setCurrentTabView('listing');
        },
      },
      {
        label: 'Seasonality',
        value: 'seasonality',
        component: SeasonalityTabSection,
        onclick: () => {
          setCurrentTabView('seasonality');
        },
      },
      {
        label: 'Booking Details',
        value: 'bookingDetails',
        component: BookingDetailsTabSection,
        onclick: () => {
          setCurrentTabView('bookingDetails');
        },
      },
      {
        label: 'Similar Listings',
        value: 'similarListings',
        component: SimilarListingTabSection,
        onclick: () => {
          setCurrentTabView('similarListings');
        },
      },
    ],
    [ListingTabSection, PerformanceTabSection]
  );

  return (
    <>
      {/* Main Modal */}
      <Modal open={open} onClose={onClose}>
        <Container
          sx={{
            ...styles.background,
            width: isMobileScreen ? '100vw' : '60vw',
            display: 'flex',
            justifyContent: 'center',
            outline: 'none !important',
            pt: '5px',
            pb: '5px',
            ...(isMobileScreen ? { paddingLeft: '5px', paddingRight: '20px' } : {}),
          }}
          className={isMobileScreen ? 'safe-mobile-height' : ''}
        >
          {showLoadingScreen && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <CircularProgress size={60} />
            </Box>
          )}
          {/* Outer Grid Container */}
          {!showLoadingScreen && (
            <Grid container justifyContent={'space-between'} alignContent={'flex-start'}>
              <TopBar
                onClose={onClose}
                isMobileScreen={isMobileScreen}
                data={currentListingData}
                title={currentListingData['name']}
                noListingData={noListingData}
                isDemo={isDemo}
                setIsDemo={setIsDemo}
                toggleUrlByDemoListingId={toggleUrlByDemoListingId}
                userSubscriptionPlan={user.subscriptionPlan}
                isListingRecentlyAdded={isListingRecentlyAdded}
                isListingInactive={isListingInactive}
                isListingUnlisted={isListingUnlisted}
              />

              {noListingData ? (
                <Typography variant="body1" sx={{ marginTop: '50px' }}>
                  Listing is not available!
                </Typography>
              ) : (
                <>
                  <SubSectionTabs
                    isMobile={isMobileScreen}
                    tabList={tabList}
                    currentTabView={currentTabView}
                    isMobileScreen={isMobileScreen}
                  />

                  {tabList.map(tab => (
                    <div
                      key={tab.value}
                      style={{
                        display: currentTabView === tab.value ? 'flex' : 'none',
                        justifyContent: 'center',
                        width: '100%',
                        padding: isMobileScreen ? '0px' : '20px',
                      }}
                    >
                      {tab.component}
                    </div>
                  ))}
                </>
              )}
            </Grid>
          )}
        </Container>
      </Modal>

      {/* Advanced View Modal */}
      {advancedViewOpen && (
        <AdvancedPointerView
          advancedViewOpen={advancedViewOpen}
          setAdvancedViewOpen={setAdvancedViewOpen}
          data={currentListingData}
          historicalPerfTrigger={historicalPerfTrigger}
          setHistoricalPerfTrigger={setHistoricalPerfTrigger}
          setSubscriptionModalOpen={setSubscriptionModalOpen}
          dynamicOccupancyRate={dynamicOccupancyRate}
          dynamicRev={dynamicRev}
          dynamicADR={dynamicADR}
          getMonthlyKeyMetrics={getMonthlyKeyMetrics}
          ownerTotalListings={ownerTotalListings as ListingLegacy[]}
          currency={currency}
          isDisabled={isDisabled}
          handleOnSampleClick={handleOnSampleClick}
          setTooltipOpen={setTooltipOpen}
        />
      )}
    </>
  );
};

export default IndividualListingModalV2;
